import styles from './../Components.module.scss';

import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Column, TableInstance } from 'react-table';

import {
  CURRENCY_PARAMS,
  CurrencyCell,
  DateCell,
  NUMBER_PARAMS,
  TextCell,
} from '@work4all/components';
import {
  EditableCell as EditableCellImported,
  EditableCellProps,
} from '@work4all/components/lib/dataDisplay/basic-table/components/row-render/components/editable-cell/EditableCell';

import { useCustomFieldsConfigContext } from '@work4all/data/lib/custom-fields';
import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';
import { useEntityJsonSchema } from '@work4all/data/lib/json-schema/EntityJsonSchemasContext';

import { ServiceContractPosition } from '@work4all/models/lib/Classes/ServiceContractPosition.entity';
import { AddServiceContractPosTypeOfPos } from '@work4all/models/lib/Enums/AddServiceContractPosTypeOfPos.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ServiceContractPositionKind } from '@work4all/models/lib/Enums/ServiceContractPositionKind.enum';

import { useDeepMemo } from '@work4all/utils/lib/hooks/use-deep-memo';

import { settings, useSetting } from '../../../../../../../../../../settings';
import { DEFAULT_VISIBLE_ERP_COLUMNS } from '../../../../../../../../../../settings/settings';
import { CostCenterCell } from '../../../../../../components/table-cells/CostCenterCell';
import { ServiceContractPositionKindCell } from '../../../../../../components/table-cells/ServiceContractPositionKindCell';
// ToDo: WW-7459 Move to shared shadow folder (2 below)
import { CustomCell } from '../../../../../../erp/components/tab-panels/positions/components/hooks/components/CustomCell';
import { useResetColumns } from '../../../../../../erp/components/tab-panels/positions/components/hooks/use-reset-columns';
import { ShadowServiceContractObjectAddComponentArgs } from '../../../../../hooks/use-shadow-service-contract-object-api';
import { useShadowServiceContractObjectApiContext } from '../../../../../hooks/use-shadow-service-contract-object-api-context';
import {
  EditTableColumns,
  EditTableEntry,
  IdArray,
  IEditTable,
  OnEditComponent,
} from '../edit-table/types';
import { ArticlePickerCell } from '../positions-table/ArticlePickerCell';

import { NumberHeader } from './components/NumberHeader';

const EditableCell: React.FC<Partial<EditableCellProps>> = EditableCellImported;

const pickerStyles = {
  margin: 0,
};

type PositionColumnData = EditTableColumns<ServiceContractPosition> & {
  title?: string;
};

interface UsePositionsColumnsProps {
  tableInstanceRef: React.RefObject<TableInstance<object>>;
  allowedColumns?: string[];
  total: number;
  onAddComponent: (
    context: ShadowServiceContractObjectAddComponentArgs
  ) => void;
  onEditComponent: (result: OnEditComponent<ServiceContractPosition>) => void;
  onRemoveComponent: (positionIds: IdArray) => void;
  disabled?: boolean;
  editTableRef: React.RefObject<IEditTable>;
  showPositionInfo?: boolean;
}

export const KINDS_TO_DISABLE_PICKERS = [
  ServiceContractPositionKind.UEBERSCHRIFT,
  ServiceContractPositionKind.TEXTZEILE,
  AddServiceContractPosTypeOfPos.TEXTLINE,
  AddServiceContractPosTypeOfPos.HEADLINE,
];

export const usePositionsColumns = (props: UsePositionsColumnsProps) => {
  const {
    tableInstanceRef,
    allowedColumns,
    total,
    onAddComponent,
    onEditComponent,
    disabled,
    editTableRef,
  } = props;

  const { t } = useTranslation();

  const schema = useEntityJsonSchema(Entities.serviceContractPosition);
  const { customFields } = useCustomFieldsConfigContext();

  const shadowServiceContractObject =
    useShadowServiceContractObjectApiContext();

  const purchasePriceTotal = useMemo(() => {
    const positions = shadowServiceContractObject.positions;
    if (!positions?.length) return 0;
    return positions.reduce(
      (prev, cur) => prev + cur.purchasePrice * cur.amountDecimal,
      0
    );
  }, [shadowServiceContractObject.positions]);

  const servicePriceTotal = useMemo(() => {
    const positions = shadowServiceContractObject.positions;
    if (!positions?.length) return 0;
    return positions.reduce((prev, cur) => prev + cur.servicePricePerYear, 0);
  }, [shadowServiceContractObject.positions]);

  const columnSettings = useSetting(settings.serviceContractPositionsColumn());

  const width = useDeepMemo(
    () => columnSettings.value.width,
    [columnSettings.value.width]
  );

  const isCellEditable = (
    cellProps: Cell<ServiceContractPosition & EditTableEntry>
  ) => {
    return (
      cellProps.row.original.positionKind !==
        ServiceContractPositionKind.UEBERSCHRIFT &&
      cellProps.row.original.positionKind !==
        ServiceContractPositionKind.TEXTZEILE
    );
  };

  const columns = useMemo(() => {
    const baseColumns: PositionColumnData[] = [
      {
        Header: NumberHeader,
        title: t('COMMON.ERP.NUMBER'),
        accessor: 'number',
        width: remToPx(5),
        type: 'text',
        Cell: TextCell,
      },
      {
        Header: t('COMMON.DATE'),
        accessor: 'date',
        width: remToPx(5),
        type: 'date',
        Cell: DateCell,
      },
      {
        Header: t('COMMON.ERP.ARTICLE_NUMBER'),
        accessor: 'articleNumber',
        width: remToPx(7),
        type: 'text',
        Cell: EditableCell,
      },
      {
        Header: t('COMMON.ERP.DESCRIPTION'),
        accessor: 'longtext',
        width: remToPx(25),
        type: 'text',
        isLongText: true,
        multiLinesRowKindsAllowed: [
          ServiceContractPositionKind.UEBERSCHRIFT,
          ServiceContractPositionKind.TEXTZEILE,
        ],
        shouldRender: (position: ServiceContractPosition & EditTableEntry) => {
          return (
            position.cacheOnly && !position.longtext && !position.articleNumber
          );
        },
        render: (
          _cell: Cell<ServiceContractPosition & EditTableEntry>,
          { isEditMode, tableInstance }
        ) => {
          return (
            <ArticlePickerCell
              cell={_cell}
              isEditMode={isEditMode}
              onAddComponent={(value) => {
                if (
                  value.typeOfPosition ===
                  AddServiceContractPosTypeOfPos.DEFAULT
                ) {
                  setTimeout(() => {
                    editTableRef.current?.setEditMode('undefined', 'longtext', [
                      'longtext',
                    ]);
                  }, 100);
                }
                onAddComponent(value);
              }}
              tableInstance={tableInstance}
            />
          );
        },
      },
      {
        Header: t('COMMON.ERP.AMOUNT'),
        accessor: 'amountDecimal',
        id: 'amountDecimal',
        width: remToPx(5),
        ...NUMBER_PARAMS,
        type: 'number',
        Cell: (cellProps) =>
          isCellEditable(cellProps) ? <EditableCell {...cellProps} /> : null,
      },
      {
        Header: t('FIELDS.E_Price'),
        accessor: 'price',
        id: 'price',
        width: remToPx(6),
        ...CURRENCY_PARAMS,
        type: 'number',
        Cell: (cellProps) =>
          isCellEditable(cellProps) ? <EditableCell {...cellProps} /> : null,
        Footer: (footerProps) => {
          return (
            <CurrencyCell
              value={total || 0}
              {...footerProps}
              hideUnit
              className={styles.titleFooter}
            />
          );
        },
      },
      {
        Header: t('INPUTS.RABAT'),
        accessor: 'discount',
        id: 'discount',
        width: remToPx(6),
        ...CURRENCY_PARAMS,
        type: 'number',
        Cell: (cellProps) =>
          isCellEditable(cellProps) ? <EditableCell {...cellProps} /> : null,
      },
      {
        Header: t('FIELDS.factor'),
        accessor: 'factor',
        id: 'factor',
        width: remToPx(6),
        ...NUMBER_PARAMS,
        type: 'number',
        filterSubgroupPath: ['COMMON.CALCULATION'],
        Cell: (cellProps) =>
          isCellEditable(cellProps) ? <EditableCell {...cellProps} /> : null,
      },
      {
        Header: t('MASK.COMMENT'),
        accessor: 'comment',
        id: 'comment',
        width: remToPx(15),
        type: 'text',
      },
      {
        Header: t('COMMON.KIND'),
        accessor: 'positionKind',
        id: 'positionKind',
        width: remToPx(15),
        type: 'picker',
        render: (
          cell: Cell<ServiceContractPosition & EditTableEntry>,
          { isEditMode }
        ) => {
          // Only show the picker if an article is selected or positionKind is already set
          const hasArticle = !!cell.row.original.articleId;
          const hasPositionKind = !!cell.row.original.positionKind;

          if (
            KINDS_TO_DISABLE_PICKERS.includes(cell.row.original.positionKind) ||
            (!hasArticle && !hasPositionKind)
          ) {
            return null;
          }

          return (
            <ServiceContractPositionKindCell
              value={cell?.row?.original?.positionKind}
              onChange={(value) => {
                onEditComponent({
                  position: {
                    id: cell.row.original.id,
                    [cell.column.id]: value ?? null,
                  },
                });
              }}
              className={clsx(styles.customCell, {
                [styles.justLabel]: !isEditMode,
              })}
              style={{
                ...pickerStyles,
                background: isEditMode ? undefined : 'unset',
                border: isEditMode ? undefined : 'unset',
              }}
              disabled={disabled}
            />
          );
        },
      },
      // Article
      {
        Header: t('FIELDS.manufacturerNumber'),
        accessor: 'manufacturerNumber',
        width: remToPx(7),
        filterSubgroupPath: ['COMMON.ARTICLE'],
        type: 'text',
      },
      {
        Header: t('FIELDS.shortText'),
        accessor: 'shortText',
        width: remToPx(7),
        filterSubgroupPath: ['COMMON.ARTICLE'],
        type: 'text',
      },
      {
        Header: t('COMMON.COSTCENTER'),
        accessor: 'costCenterId',
        width: remToPx(12),
        filterSubgroupPath: ['COMMON.CALCULATION'],
        type: 'picker',
        render: (
          cell: Cell<ServiceContractPosition & EditTableEntry>,
          { isEditMode }
        ) => (
          <CostCenterCell
            label={(value) =>
              value?.number && value?.name
                ? `${value.number} | ${value.name}`
                : ''
            }
            value={cell?.row?.original?.costCenter}
            className={clsx(styles.customCell, {
              [styles.justLabel]: !isEditMode,
            })}
            style={{
              ...pickerStyles,
              background: isEditMode ? undefined : 'unset',
              border: isEditMode ? undefined : 'unset',
            }}
            onChange={(value) => {
              onEditComponent({
                position: {
                  id: cell.row.original.id,
                  [cell.column.id]: value?.id || 0,
                },
              });
            }}
            hover
            disabled={
              disabled ||
              KINDS_TO_DISABLE_PICKERS.includes(cell.row.original.positionKind)
            }
          />
        ),
      },
      {
        Header: t('SERVICECONTRACT.MAINTENANCE_SHARE'),
        accessor: 'percent',
        width: remToPx(6),
        ...NUMBER_PARAMS,
        type: 'number',
        filterSubgroupPath: ['COMMON.CALCULATION'],
        Cell: (cellProps) =>
          isCellEditable(cellProps) ? (
            <EditableCell
              {...cellProps}
              value={Math.round(cellProps.value || 0)}
              type="number"
              isEditable
            />
          ) : null,
        cellParams: {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        },
      },
      {
        Header: t('FIELDS.unit'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'article.unit.name' as any,
        width: remToPx(5),
        filterSubgroupPath: ['COMMON.ARTICLE'],
        type: 'text',
        editable: false,
      },
      // Calculation
      {
        Header: t('INPUTS.RABAT_ANNUAL_RENT'),
        accessor: 'discount2',
        id: 'discount2',
        width: remToPx(6),
        filterSubgroupPath: ['COMMON.CALCULATION'],
        ...CURRENCY_PARAMS,
        type: 'number',
        Cell: (cellProps) =>
          isCellEditable(cellProps) ? <EditableCell {...cellProps} /> : null,
      },
      {
        Header: t('SERVICECONTRACT.PRICE_TOTAL'),
        accessor: 'servicePricePerYear',
        id: 'servicePricePerYear',
        width: remToPx(6),
        filterSubgroupPath: ['COMMON.CALCULATION'],
        ...CURRENCY_PARAMS,
        type: 'number',
        Cell: (cellProps) =>
          isCellEditable(cellProps) ? <EditableCell {...cellProps} /> : null,
        Footer: (footerProps) => {
          return (
            <CurrencyCell
              value={servicePriceTotal}
              {...footerProps}
              hideUnit
              className={styles.titleFooter}
            />
          );
        },
      },
      // Other
      {
        Header: t('FIELDS.amountSupplier'),
        accessor: 'amountSupplier',
        id: 'amountSupplier',
        width: remToPx(5),
        filterSubgroupPath: ['COMMON.MISC'],
        ...NUMBER_PARAMS,
        type: 'number',
        Cell: (cellProps) =>
          isCellEditable(cellProps) ? <EditableCell {...cellProps} /> : null,
      },
      {
        Header: t('FIELDS.supplier'),
        filterSubgroupPath: ['COMMON.MISC'],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'supplier.name' as any,
        width: remToPx(10),
        editable: false,
      },
      {
        Header: t('FIELDS.EK_Price'),
        accessor: 'purchasePrice',
        id: 'purchasePrice',
        filterSubgroupPath: ['COMMON.MISC'],
        width: remToPx(6),
        ...CURRENCY_PARAMS,
        type: 'number',
        Cell: (cellProps) =>
          isCellEditable(cellProps) ? <EditableCell {...cellProps} /> : null,
        Footer: (footerProps) => {
          return (
            <CurrencyCell
              value={purchasePriceTotal}
              {...footerProps}
              hideUnit
              className={styles.titleFooter}
            />
          );
        },
      },
    ];

    const filtered = [...baseColumns].filter((col) => {
      return (
        Boolean(col) &&
        (allowedColumns
          ? allowedColumns.includes(col.accessor as string)
          : true)
      );
    });

    if (!allowedColumns)
      Object.entries(width).forEach((colWidth) => {
        const [prop, value] = colWidth;
        const colIdx = filtered.findIndex((col) => col.accessor === prop);
        if (filtered[colIdx]) {
          filtered[colIdx].width = value;
        }
      });

    filtered.forEach((col) => {
      if (typeof col.accessor !== 'string') return;
      const schemaField = schema?.properties?.[col.accessor];
      col.schema = schemaField;
    });

    return [...filtered];
  }, [
    t,
    allowedColumns,
    width,
    onAddComponent,
    editTableRef,
    total,
    disabled,
    onEditComponent,
    servicePriceTotal,
    purchasePriceTotal,
    schema?.properties,
  ]);

  const allColumns = useMemo(() => {
    if (!customFields || allowedColumns) return columns;
    return [
      ...columns,
      ...customFields
        .sort((a, b) => (a.name > b.name ? 1 : -1))
        .map((cField, order) => ({
          Header: cField.name,
          accessor: cField.id.toString(),
          width: 120,
          filterSubgroupPath: ['MASK.INDIVIDUAL'],
          order,
          Cell: (data) => <CustomCell field={cField} cellData={data} />,
        })),
    ] as Column<ServiceContractPosition>[];
  }, [allowedColumns, columns, customFields]);

  // resetColumns
  const resetColumns = useResetColumns({
    columnSettings,
    tableInstanceRef,
    defaultColumns: DEFAULT_VISIBLE_ERP_COLUMNS,
  });
  return {
    columns: allColumns,
    resetColumns,
  };
};
